import * as THREE from "three";

import {
  createFirstYardVisualKit,
  type FirstYardVisualKit,
} from "../../assets/factories/first-yard/index.ts";
import { createArcadeIdleJuice } from "../juice/arcade-idle-juice.ts";
import type {
  FirstYardManualChainPresentationEvent,
  FirstYardManualChainPresentationSnapshot,
} from "./presentation-contract.ts";
import { classifyFirstYardTrajectory } from "./presentation-contract.ts";

const FIXED_STEP_SECONDS = 0.05;
const PLAYER_SPEED_WU_PER_SECOND = 3.35;
const CAMERA_FOV_DEGREES = 30;
const CAMERA_YAW_RADIANS = THREE.MathUtils.degToRad(42);
const CAMERA_PITCH_RADIANS = THREE.MathUtils.degToRad(55);
const CAMERA_DAMPING_SECONDS = 0.22;
const LOOK_AHEAD_WU = 1.12; // 1.4 m at the project's 1 WU = 1.25 m scale.
const MAX_PIXEL_RATIO = 2;
const ACTOR_SAFE_NDC_X = 0.68;
const ACTOR_SAFE_NDC_Y = 0.72;
const ROUTE_FIT_NDC_X = 0.86;
const ROUTE_FIT_NDC_Y = 0.8;

const WALKABLE_BOUNDS = Object.freeze({ xMin: -5.2, xMax: 5.2, zMin: -3.5, zMax: 5.4 });
const CLOSED_OBSTACLES = Object.freeze([
  Object.freeze({ xMin: 1.4, xMax: 4.6, zMin: -3.2, zMax: 0.45 }),
  Object.freeze({ xMin: -0.85, xMax: 0.85, zMin: 5.1, zMax: 6.05 }),
]);
// Exact D-016 chicken-pen-01-q2 authored queue point (-250, -823 milliWU).
// It is clear of the pen, field, market, and guided pad visual footprints while
// keeping the opening actor on the authored field-to-pen route.
const INITIAL_POSITION = Object.freeze({ x: -0.25, z: -0.823 });
const YARD_FIT_ANCHOR = new THREE.Vector3(-0.35, 0, 0.75);
const ROUTE_LANDMARKS = Object.freeze([
  Object.freeze({ x: -4.102, z: -1.473 }),
  Object.freeze({ x: 1, z: -1.473 }),
  Object.freeze({ x: -1.551, z: 2.945 }),
]);
const LOCKED_GATE_LANDMARK = Object.freeze({ x: 0, z: 5.4 });

export interface WalkaboutMovementIntent {
  readonly x: number;
  readonly y?: number;
  readonly z?: number;
}

export interface WalkaboutPresentationSnapshot {
  readonly position: Readonly<{ x: number; z: number }>;
  readonly facing: number;
  readonly facingVector: Readonly<{ x: number; z: number }>;
  readonly facingRadians: number;
  readonly moving: boolean;
  readonly objectiveId: "visit-field";
  readonly cargo: Readonly<{ kind: "none"; count: 0; capacity: 4 }>;
  readonly cash: 0;
  readonly reducedMotion: boolean;
}

export interface FirstYardSceneController {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  stepFixed(intent: Readonly<WalkaboutMovementIntent>, stepSeconds: number): void;
  projectManualChain(
    snapshot: Readonly<FirstYardManualChainPresentationSnapshot>,
    events: readonly Readonly<FirstYardManualChainPresentationEvent>[],
  ): void;
  render(alpha: number, elapsedSeconds: number): void;
  resize(width: number, height: number, pixelRatio: number): void;
  setReducedMotion(reducedMotion: boolean): void;
  snapshot(): Readonly<WalkaboutPresentationSnapshot>;
  dispose(): void;
}

interface AnimatedObject {
  readonly object: THREE.Object3D;
  readonly rotationX: number;
  readonly rotationY: number;
  readonly rotationZ: number;
  readonly positionY: number;
  readonly scale: THREE.Vector3;
}

interface DustParticle {
  readonly mesh: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  bornAtSeconds: number;
}

interface OccluderMaterialState {
  readonly opacity: number;
  readonly transparent: boolean;
  readonly depthWrite: boolean;
}

interface ManualChainEffect {
  readonly eventId: number;
  readonly kind: string;
  readonly bornAtSeconds: number;
  readonly anchor: THREE.Vector3;
  readonly plotId: number | null;
  readonly quantity: number;
  readonly playerOrigin: THREE.Vector3;
}

interface TrajectoryPool {
  readonly mesh: THREE.InstancedMesh;
  cursor: number;
}

function freezeSnapshot(
  x: number,
  z: number,
  facingRadians: number,
  moving: boolean,
  reducedMotion: boolean,
): Readonly<WalkaboutPresentationSnapshot> {
  const facingVector = Object.freeze({
    x: Math.sin(facingRadians),
    z: Math.cos(facingRadians),
  });
  return Object.freeze({
    position: Object.freeze({ x, z }),
    facing: facingRadians,
    facingVector,
    facingRadians,
    moving,
    objectiveId: "visit-field",
    cargo: Object.freeze({ kind: "none", count: 0, capacity: 4 }),
    cash: 0,
    reducedMotion,
  });
}

function isInsideClosedObstacle(x: number, z: number): boolean {
  return CLOSED_OBSTACLES.some(
    (obstacle) =>
      x >= obstacle.xMin && x <= obstacle.xMax && z >= obstacle.zMin && z <= obstacle.zMax,
  );
}

function isWalkable(x: number, z: number): boolean {
  return (
    x >= WALKABLE_BOUNDS.xMin &&
    x <= WALKABLE_BOUNDS.xMax &&
    z >= WALKABLE_BOUNDS.zMin &&
    z <= WALKABLE_BOUNDS.zMax &&
    !isInsideClosedObstacle(x, z)
  );
}

function clampToWalkableAxis(
  currentX: number,
  currentZ: number,
  deltaX: number,
  deltaZ: number,
): Readonly<{ x: number; z: number }> {
  let x = THREE.MathUtils.clamp(currentX + deltaX, WALKABLE_BOUNDS.xMin, WALKABLE_BOUNDS.xMax);
  let z = currentZ;
  if (!isWalkable(x, z)) {
    x = currentX;
  }

  z = THREE.MathUtils.clamp(currentZ + deltaZ, WALKABLE_BOUNDS.zMin, WALKABLE_BOUNDS.zMax);
  if (!isWalkable(x, z)) {
    z = currentZ;
  }
  return Object.freeze({ x, z });
}

function asObjectList(value: THREE.Object3D | readonly THREE.Object3D[]): readonly THREE.Object3D[] {
  return value instanceof THREE.Object3D ? [value] : value;
}

function captureAnimatedObjects(value: THREE.Object3D | readonly THREE.Object3D[]): AnimatedObject[] {
  return asObjectList(value).map((object) => ({
    object,
    rotationX: object.rotation.x,
    rotationY: object.rotation.y,
    rotationZ: object.rotation.z,
    positionY: object.position.y,
    scale: object.scale.clone(),
  }));
}

function forEachMaterial(object: THREE.Object3D, visit: (material: THREE.Material) => void): void {
  object.traverse((descendant) => {
    if (!(descendant instanceof THREE.Mesh)) {
      return;
    }
    const materials = Array.isArray(descendant.material) ? descendant.material : [descendant.material];
    for (const material of materials) {
      visit(material);
    }
  });
}

function normalizedIntent(intent: Readonly<WalkaboutMovementIntent>): Readonly<{ x: number; z: number }> {
  const rawX = Number.isFinite(intent.x) ? intent.x : 0;
  const requestedZ = intent.z ?? intent.y ?? 0;
  const rawZ = Number.isFinite(requestedZ) ? requestedZ : 0;
  const length = Math.hypot(rawX, rawZ);
  if (length <= Number.EPSILON) {
    return Object.freeze({ x: 0, z: 0 });
  }
  const divisor = Math.max(1, length);
  return Object.freeze({ x: rawX / divisor, z: rawZ / divisor });
}

function shortestAngle(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

function findMeshByMaterialName(root: THREE.Object3D, materialName: string): THREE.Mesh {
  let match: THREE.Mesh | null = null;
  root.traverse((node) => {
    if (match !== null || !(node instanceof THREE.Mesh) || Array.isArray(node.material)) return;
    if (node.material.name === materialName) match = node;
  });
  if (match === null) throw new Error(`Missing authored trajectory material: ${materialName}`);
  return match;
}

function setTrajectoryMatrix(
  pool: TrajectoryPool,
  source: THREE.Vector3,
  destination: THREE.Vector3,
  progress: number,
  arcHeight: number,
  scaleValue: number,
  spin: number,
  matrix: THREE.Matrix4,
  position: THREE.Vector3,
  quaternion: THREE.Quaternion,
  scale: THREE.Vector3,
): void {
  if (pool.cursor >= pool.mesh.count) return;
  position.lerpVectors(source, destination, progress);
  position.y += Math.sin(progress * Math.PI) * arcHeight;
  quaternion.setFromEuler(new THREE.Euler(progress * spin, progress * spin * 0.7, progress * spin * 0.35));
  scale.setScalar(Math.max(0.001, scaleValue));
  matrix.compose(position, quaternion, scale);
  pool.mesh.setMatrixAt(pool.cursor, matrix);
  pool.cursor += 1;
}

export function createFirstYardScene(options: {
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly reducedMotion?: boolean;
}): FirstYardSceneController {
  const { renderer, canvas } = options;
  const visualKit: FirstYardVisualKit = createFirstYardVisualKit();
  const kit = {
    root: visualKit.root,
    player: visualKit.player.root,
    chickens: visualKit.chickens.map((chicken) => chicken.root),
    corn: visualKit.corn.map((corn) => corn.leaves),
    guidedPad: visualKit.guidedPad.root,
    manualChain: visualKit.manualChain,
    occluders: visualKit.occluders,
    dispose: () => visualKit.dispose(),
  };
  const scene = new THREE.Scene();
  scene.name = "sunpatch-first-yard-scene";
  // Grok juice: warmer, sunnier commercial-ad sky.
  scene.background = new THREE.Color(0xa8dde3);
  scene.fog = new THREE.Fog(0xa8dde3, 20, 48);
  scene.add(kit.root);

  const camera = new THREE.PerspectiveCamera(CAMERA_FOV_DEGREES, 1, 0.1, 80);
  camera.name = "sunpatch-first-yard-camera";
  camera.up.set(0, 1, 0);
  camera.rotation.order = "YXZ";

  const warmKey = new THREE.DirectionalLight(0xffe2ad, 3.85);
  warmKey.name = "warm-key";
  warmKey.position.set(-7, 13, -8);
  warmKey.castShadow = true;
  warmKey.shadow.mapSize.set(1024, 1024);
  warmKey.shadow.camera.left = -10;
  warmKey.shadow.camera.right = 10;
  warmKey.shadow.camera.top = 10;
  warmKey.shadow.camera.bottom = -10;
  warmKey.shadow.camera.near = 1;
  warmKey.shadow.camera.far = 30;
  warmKey.shadow.bias = -0.00025;
  const coolFill = new THREE.DirectionalLight(0x9ad4e8, 1.35);
  coolFill.name = "cool-fill";
  coolFill.position.set(9, 7, 7);
  const rim = new THREE.DirectionalLight(0xfff6d8, 0.55);
  rim.name = "rim-sparkle";
  rim.position.set(2, 4, -10);
  const groundBounce = new THREE.HemisphereLight(0xd4f2ea, 0xc48a52, 1.75);
  groundBounce.name = "ground-bounce";
  scene.add(warmKey, coolFill, rim, groundBounce);

  const juice = createArcadeIdleJuice();
  scene.add(juice.root);

  const dustGeometry = new THREE.CircleGeometry(0.13, 7);
  dustGeometry.rotateX(-Math.PI / 2);
  const dustGroup = new THREE.Group();
  dustGroup.name = "bounded-footstep-dust";
  const dustParticles: DustParticle[] = [];
  for (let index = 0; index < 12; index += 1) {
    const material = new THREE.MeshBasicMaterial({
      color: index % 2 === 0 ? 0xd7af75 : 0xc8945f,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(dustGeometry, material);
    mesh.name = `footstep-dust-${index}`;
    mesh.visible = false;
    mesh.renderOrder = 2;
    dustGroup.add(mesh);
    dustParticles.push({ mesh, bornAtSeconds: Number.NEGATIVE_INFINITY });
  }
  scene.add(dustGroup);

  const manualVfxCapacity = 28;
  const leafVfxGeometry = new THREE.ConeGeometry(0.055, 0.16, 5);
  const leafVfxMaterial = new THREE.MeshBasicMaterial({ color: 0x8fd84f });
  const leafVfx = new THREE.InstancedMesh(leafVfxGeometry, leafVfxMaterial, manualVfxCapacity);
  leafVfx.name = "manual-chain-leaf-fleck-pool";
  leafVfx.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  leafVfx.frustumCulled = false;
  const rewardVfxGeometry = new THREE.OctahedronGeometry(0.08, 0);
  const rewardVfxMaterial = new THREE.MeshBasicMaterial({ color: 0xffc857 });
  const rewardVfx = new THREE.InstancedMesh(rewardVfxGeometry, rewardVfxMaterial, manualVfxCapacity);
  rewardVfx.name = "manual-chain-reward-mote-pool";
  rewardVfx.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  rewardVfx.frustumCulled = false;
  const vfxMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
  for (let index = 0; index < manualVfxCapacity; index += 1) {
    leafVfx.setMatrixAt(index, vfxMatrix);
    rewardVfx.setMatrixAt(index, vfxMatrix);
  }
  leafVfx.instanceMatrix.needsUpdate = true;
  rewardVfx.instanceMatrix.needsUpdate = true;
  scene.add(leafVfx, rewardVfx);

  const trajectoryCapacity = 8;
  const createTrajectoryPool = (
    name: string,
    authoredSource: THREE.Object3D,
    materialName: string,
  ): TrajectoryPool => {
    const sourceMesh = findMeshByMaterialName(authoredSource, materialName);
    const mesh = new THREE.InstancedMesh(sourceMesh.geometry, sourceMesh.material, trajectoryCapacity);
    mesh.name = name;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.userData.motionMode = "arc";
    for (let index = 0; index < trajectoryCapacity; index += 1) mesh.setMatrixAt(index, vfxMatrix);
    mesh.instanceMatrix.needsUpdate = true;
    scene.add(mesh);
    return { cursor: 0, mesh };
  };
  const cornTrajectory = createTrajectoryPool(
    "manual-chain-corn-trajectory-pool",
    kit.manualChain.cargo[0]!.corn,
    "sunpatch-corn-material",
  );
  const eggTrajectory = createTrajectoryPool(
    "manual-chain-egg-trajectory-pool",
    kit.manualChain.cargo[0]!.egg,
    "sunpatch-cream-material",
  );
  const cashTrajectory = createTrajectoryPool(
    "manual-chain-cash-trajectory-pool",
    kit.manualChain.marketCash,
    "sunpatch-cash-material",
  );
  const reducedMotionStateCue = new THREE.Object3D();
  reducedMotionStateCue.name = "manual-chain-reduced-motion-state-cue";
  reducedMotionStateCue.visible = false;
  reducedMotionStateCue.userData.presentationEquivalent = "anchored-source-destination-crossfade";
  scene.add(reducedMotionStateCue);

  const playerBaseY = kit.player.position.y;
  const playerBaseRotationZ = kit.player.rotation.z;
  kit.player.position.x = INITIAL_POSITION.x;
  kit.player.position.z = INITIAL_POSITION.z;
  const playerParts = captureAnimatedObjects([
    visualKit.player.leftArm,
    visualKit.player.rightArm,
    visualKit.player.leftLeg,
    visualKit.player.rightLeg,
  ]);
  const chickenParts = captureAnimatedObjects(
    visualKit.chickens.flatMap((chicken) => [chicken.head, chicken.leftWing, chicken.rightWing]),
  );
  const cornParts = captureAnimatedObjects(visualKit.corn.map((corn) => corn.leaves));
  const padParts = captureAnimatedObjects(visualKit.guidedPad.root);
  const cargoParts = captureAnimatedObjects(kit.manualChain.cargo.map((unit) => unit.root));
  const stagedCornParts = captureAnimatedObjects(kit.manualChain.stagedCorn);
  const nestEggParts = captureAnimatedObjects(kit.manualChain.nestEggs);
  const marketReceiverParts = captureAnimatedObjects(kit.manualChain.marketReceiver);
  const penPadParts = captureAnimatedObjects([
    kit.manualChain.penExtensionPad.active,
    kit.manualChain.penExtensionPad.chevrons,
  ]);
  const occluderMaterialStates = new Map<THREE.Material, OccluderMaterialState>();
  const preparedOccluderMeshes = new Set<THREE.Mesh>();
  const replacedOccluderMaterials = new Set<THREE.Material>();
  const clonedOccluderMaterials = new Set<THREE.Material>();
  for (const occluder of kit.occluders) {
    occluder.traverse((descendant) => {
      if (!(descendant instanceof THREE.Mesh) || preparedOccluderMeshes.has(descendant)) {
        return;
      }
      preparedOccluderMeshes.add(descendant);
      const originalMaterials = Array.isArray(descendant.material)
        ? descendant.material
        : [descendant.material];
      for (const material of originalMaterials) {
        replacedOccluderMaterials.add(material);
      }
      descendant.material = Array.isArray(descendant.material)
        ? descendant.material.map((material) => material.clone())
        : descendant.material.clone();
      const clones = Array.isArray(descendant.material) ? descendant.material : [descendant.material];
      for (const clone of clones) clonedOccluderMaterials.add(clone);
    });
    forEachMaterial(occluder, (material) => {
      if (!occluderMaterialStates.has(material)) {
        occluderMaterialStates.set(material, {
          opacity: material.opacity,
          transparent: material.transparent,
          depthWrite: material.depthWrite,
        });
      }
    });
  }
  const reachableMaterials = new Set<THREE.Material>();
  forEachMaterial(kit.root, (material) => reachableMaterials.add(material));
  const detachedOwnedMaterials = [...replacedOccluderMaterials].filter(
    (material) => !reachableMaterials.has(material),
  );

  const raycaster = new THREE.Raycaster();
  const playerWorldPosition = new THREE.Vector3();
  const cameraTarget = new THREE.Vector3();
  const desiredCameraTarget = new THREE.Vector3();
  const cameraOffset = new THREE.Vector3();
  const projectionProbe = new THREE.Vector3();
  const gateFitCorners: THREE.Vector3[] = [];
  const penFitCorners: THREE.Vector3[] = [];
  let previousX: number = INITIAL_POSITION.x;
  let previousZ: number = INITIAL_POSITION.z;
  let currentX: number = INITIAL_POSITION.x;
  let currentZ: number = INITIAL_POSITION.z;
  let previousFacing = Math.PI;
  let currentFacing = Math.PI;
  let moving = false;
  let reducedMotion = options.reducedMotion ?? false;
  let simulationSeconds = 0;
  let dustDistanceAccumulator = 0;
  let nextDustParticle = 0;
  let lastRenderElapsedSeconds: number | null = null;
  let baseCameraDistance = 15.5;
  let cameraDistance = baseCameraDistance;
  let disposed = false;
  let currentSnapshot = freezeSnapshot(currentX, currentZ, currentFacing, moving, reducedMotion);
  let manualSnapshot: Readonly<FirstYardManualChainPresentationSnapshot> | null = null;
  const seenManualEventIds = new Set<number>();
  const manualEffects: ManualChainEffect[] = [];

  function effectAnchor(kind: string): THREE.Vector3 {
    if (/harvest|corn|plot/i.test(kind)) return new THREE.Vector3(-4.102, 0.72, -1.473);
    if (/stage|feed|tend|chicken|egg|collect/i.test(kind)) return new THREE.Vector3(1, 0.92, -1.273);
    if (/sale|cash|market/i.test(kind)) return new THREE.Vector3(-1.551, 1.05, 2.945);
    if (/pad|extension|afford/i.test(kind)) return new THREE.Vector3(-3.853, 0.45, 2.945);
    return new THREE.Vector3(currentX, 1.1, currentZ);
  }

  function hasPortraitPenFocus(): boolean {
    if (camera.aspect >= 0.75 || manualSnapshot === null) return false;
    const interaction = manualSnapshot.interaction?.kind;
    return (
      manualSnapshot.objectiveId === "feed-chickens" ||
      manualSnapshot.objectiveId === "collect-eggs" ||
      interaction === "stage" ||
      interaction === "tend" ||
      interaction === "collect"
    );
  }

  const shadowMap = renderer.shadowMap;
  const originalShadowEnabled = shadowMap.enabled;
  const originalShadowType = shadowMap.type;
  shadowMap.enabled = true;
  shadowMap.type = THREE.PCFSoftShadowMap;

  function updateCameraPosition(target: THREE.Vector3): void {
    const cameraYaw = hasPortraitPenFocus() ? THREE.MathUtils.degToRad(18) : CAMERA_YAW_RADIANS;
    const horizontalDistance = Math.cos(CAMERA_PITCH_RADIANS) * cameraDistance;
    cameraOffset.set(
      Math.sin(cameraYaw) * horizontalDistance,
      Math.sin(CAMERA_PITCH_RADIANS) * cameraDistance,
      Math.cos(cameraYaw) * horizontalDistance,
    );
    camera.position.copy(target).add(cameraOffset);
    camera.rotation.set(-CAMERA_PITCH_RADIANS, cameraYaw, 0);
    camera.updateMatrixWorld();
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.near = cameraDistance + 3;
      scene.fog.far = cameraDistance + 28;
    }
  }

  function fitCameraDistance(target: THREE.Vector3, renderX: number, renderZ: number): void {
    cameraDistance = baseCameraDistance;
    const actorPoint = { x: renderX, z: renderZ };
    const penFocus = hasPortraitPenFocus();
    const fitPoints = [...(penFocus ? ROUTE_LANDMARKS.slice(1) : ROUTE_LANDMARKS), actorPoint];
    for (let iteration = 0; iteration < 128; iteration += 1) {
      updateCameraPosition(target);
      let requiredScale = 1;
      for (const point of fitPoints) {
        projectionProbe.set(point.x, point === actorPoint ? 1.05 : 0.45, point.z).project(camera);
        const limitX =
          point === actorPoint || (!penFocus && point === ROUTE_LANDMARKS[2])
            ? ACTOR_SAFE_NDC_X
            : penFocus && point === ROUTE_LANDMARKS[2]
              ? 0.99
              : ROUTE_FIT_NDC_X;
        const limitY =
          point === actorPoint || (!penFocus && point === ROUTE_LANDMARKS[2])
            ? ACTOR_SAFE_NDC_Y
            : penFocus && point === ROUTE_LANDMARKS[2]
              ? 0.99
              : ROUTE_FIT_NDC_Y;
        requiredScale = Math.max(
          requiredScale,
          Math.abs(projectionProbe.x) / limitX,
          Math.abs(projectionProbe.y) / limitY,
        );
      }
      if (penFocus) {
        projectionProbe.set(LOCKED_GATE_LANDMARK.x, 1.1, LOCKED_GATE_LANDMARK.z).project(camera);
        requiredScale = Math.max(
          requiredScale,
          Math.abs(projectionProbe.x) / 0.99,
          Math.abs(projectionProbe.y) / 0.99,
        );
      } else {
        for (const corner of gateFitCorners) {
          projectionProbe.copy(corner).project(camera);
          requiredScale = Math.max(
            requiredScale,
            Math.abs(projectionProbe.x) / 0.97,
            Math.abs(projectionProbe.y) / 0.94,
          );
        }
      }
      if (hasPortraitPenFocus()) {
        for (const corner of penFitCorners) {
          projectionProbe.copy(corner).project(camera);
          requiredScale = Math.max(
            requiredScale,
            Math.abs(projectionProbe.x) / 0.94,
            Math.abs(projectionProbe.y) / 0.9,
          );
        }
      }
      if (requiredScale <= 1.0001) {
        return;
      }
      // Perspective fit is nonlinear around an off-center target. Small monotonic
      // increments avoid the severe portrait over-fit caused by multiplying the
      // current distance by an NDC ratio measured at the previous distance.
      cameraDistance *= 1.0125;
    }
  }

  function computeDesiredCameraTarget(renderX: number, renderZ: number, facing: number): void {
    const lookAhead = moving ? LOOK_AHEAD_WU : 0;
    let xMin = renderX;
    let xMax = renderX;
    let zMin = renderZ;
    let zMax = renderZ;
    for (const landmark of ROUTE_LANDMARKS) {
      xMin = Math.min(xMin, landmark.x);
      xMax = Math.max(xMax, landmark.x);
      zMin = Math.min(zMin, landmark.z);
      zMax = Math.max(zMax, landmark.z);
    }
    desiredCameraTarget.set(
      (xMin + xMax) * 0.5 + Math.sin(facing) * lookAhead * 0.12,
      0.72,
      (zMin + zMax) * 0.5 + Math.cos(facing) * lookAhead * 0.12,
    );
    desiredCameraTarget.lerp(YARD_FIT_ANCHOR, 0.08);
    if (camera.aspect < 0.75) {
      if (hasPortraitPenFocus()) {
        // Keep actor, chickens, feed edge, and nest in one cause/effect frame.
        // Gate and market remain part of the fit set instead of being discarded.
        desiredCameraTarget.set(1.7, 0.72, 0.72);
      } else {
        desiredCameraTarget.x += 1.45;
        desiredCameraTarget.z += 1.0;
      }
    }
  }

  const penLandmark = scene.getObjectByName("landmark-chicken-pen-01");
  if (penLandmark !== undefined) {
    const penBounds = new THREE.Box3().setFromObject(penLandmark);
    for (const x of [penBounds.min.x, penBounds.max.x]) {
      for (const z of [penBounds.min.z, penBounds.max.z]) {
        penFitCorners.push(new THREE.Vector3(x, 0.7, z));
      }
    }
  }
  computeDesiredCameraTarget(currentX, currentZ, currentFacing);
  const lockedGate = scene.getObjectByName("landmark-yard-gate-01-locked");
  if (lockedGate !== undefined) {
    const gateBounds = new THREE.Box3().setFromObject(lockedGate);
    for (const x of [gateBounds.min.x, gateBounds.max.x]) {
      for (const y of [gateBounds.min.y, gateBounds.max.y]) {
        for (const z of [gateBounds.min.z, gateBounds.max.z]) gateFitCorners.push(new THREE.Vector3(x, y, z));
      }
    }
  } else {
    gateFitCorners.push(new THREE.Vector3(LOCKED_GATE_LANDMARK.x, 1.15, LOCKED_GATE_LANDMARK.z));
  }
  cameraTarget.copy(desiredCameraTarget);
  fitCameraDistance(cameraTarget, currentX, currentZ);
  updateCameraPosition(cameraTarget);

  function emitDust(x: number, z: number): void {
    const particle = dustParticles[nextDustParticle];
    if (particle === undefined) {
      return;
    }
    nextDustParticle = (nextDustParticle + 1) % dustParticles.length;
    const side = nextDustParticle % 2 === 0 ? -1 : 1;
    particle.bornAtSeconds = simulationSeconds;
    particle.mesh.position.set(
      x + Math.cos(currentFacing) * side * 0.13,
      0.035,
      z - Math.sin(currentFacing) * side * 0.13,
    );
    particle.mesh.rotation.z = currentFacing + side * 0.35;
    particle.mesh.scale.setScalar(0.55);
    particle.mesh.visible = true;
  }

  function resetAnimationParts(parts: readonly AnimatedObject[]): void {
    for (const part of parts) {
      part.object.rotation.set(part.rotationX, part.rotationY, part.rotationZ);
      part.object.position.y = part.positionY;
      part.object.scale.copy(part.scale);
    }
  }

  function applyManualSnapshot(snapshot: Readonly<FirstYardManualChainPresentationSnapshot>): void {
    if (snapshot.plots.length !== 8 || snapshot.cargo.capacity !== 5) {
      throw new RangeError("Manual-chain presentation requires exactly eight plots and five cargo slots");
    }
    if (!Number.isInteger(snapshot.cargo.count) || snapshot.cargo.count < 0 || snapshot.cargo.count > 5) {
      throw new RangeError("Manual-chain cargo count must be an integer from zero through five");
    }
    snapshot.plots.forEach((plot, index) => {
      const rig = kit.manualChain.plots[index];
      if (rig === undefined) throw new RangeError("Manual-chain plot projection is incomplete");
      rig.root.visible = plot.state === "ready";
      rig.clearedCue.visible = plot.state === "cleared";
      rig.cooldownCue.visible = plot.state === "cooldown";
      rig.cooldownCue.rotation.y = THREE.MathUtils.clamp(plot.cooldown01, 0, 1) * Math.PI * 2;
    });
    kit.manualChain.cargo.forEach((unit, index) => {
      unit.root.visible = index < snapshot.cargo.count && snapshot.cargo.kind !== "none";
      unit.corn.visible = snapshot.cargo.kind === "corn";
      unit.egg.visible = snapshot.cargo.kind === "egg";
    });
    kit.manualChain.stagedCorn.forEach((unit, index) => {
      unit.visible = index < snapshot.pen.stagedCorn;
    });
    const collected = new Set(snapshot.nest.collectedEggIds);
    const presentEggs = snapshot.nest.createdEggIds.filter((id) => !collected.has(id));
    kit.manualChain.nestEggs.forEach((egg, index) => {
      egg.visible = index < presentEggs.length;
    });
    kit.manualChain.marketCash.visible = snapshot.cash > 0;
    const activePad = snapshot.pad.state === "active";
    kit.manualChain.penExtensionPad.locked.visible = !activePad;
    kit.manualChain.penExtensionPad.active.visible = activePad;
    kit.guidedPad.visible = false;
  }

  function updateManualTrajectories(): void {
    const pools = [cornTrajectory, eggTrajectory, cashTrajectory] as const;
    const hidden = new THREE.Matrix4().makeScale(0, 0, 0);
    for (const pool of pools) {
      pool.cursor = 0;
      pool.mesh.userData.motionMode = reducedMotion ? "anchored-crossfade" : "arc";
      for (let index = 0; index < trajectoryCapacity; index += 1) pool.mesh.setMatrixAt(index, hidden);
    }
    reducedMotionStateCue.visible = false;
    kit.root.updateMatrixWorld(true);
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const matrix = new THREE.Matrix4();
    const nest = new THREE.Vector3(1.18, 1.08, -0.65);
    const feed = new THREE.Vector3(1.45, 0.66, -1.92);
    const marketReceiver = kit.manualChain.marketReceiver.getWorldPosition(new THREE.Vector3());
    const cashLanding = kit.manualChain.marketCash.getWorldPosition(new THREE.Vector3());
    const penPad = kit.manualChain.penExtensionPad.root.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0.42, 0));

    const draw = (
      pool: TrajectoryPool,
      source: THREE.Vector3,
      destination: THREE.Vector3,
      progress: number,
      arcHeight: number,
      spin: number,
    ): void => {
      if (reducedMotion) {
        reducedMotionStateCue.visible = true;
        reducedMotionStateCue.position.copy(destination);
        setTrajectoryMatrix(pool, source, source, 0, 0, 1 - progress, 0, matrix, position, quaternion, scale);
        setTrajectoryMatrix(pool, destination, destination, 0, 0, progress, 0, matrix, position, quaternion, scale);
      } else {
        setTrajectoryMatrix(pool, source, destination, progress, arcHeight, 1, spin, matrix, position, quaternion, scale);
      }
    };

    for (const effect of manualEffects) {
      const duration = reducedMotion ? 0.35 : 0.62;
      const age = simulationSeconds - effect.bornAtSeconds;
      if (age < 0 || age > duration) continue;
      const progress = THREE.MathUtils.clamp(age / duration, 0, 1);
      const unitCount = Math.max(1, Math.min(4, effect.quantity));
      const player = effect.playerOrigin;
      const trajectoryKind = classifyFirstYardTrajectory(effect.kind);
      const plotIndex = effect.plotId === null ? -1 : kit.manualChain.plots.findIndex((plot) => plot.root.userData.assetNodeId === `first-yard-ripe-corn-${effect.plotId}`);
      const plotSource = plotIndex >= 0
        ? kit.manualChain.plots[plotIndex]!.root.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0.65, 0))
        : new THREE.Vector3(-4.102, 0.82, -1.473);
      const offset = (index: number): THREE.Vector3 => new THREE.Vector3((index - (unitCount - 1) * 0.5) * 0.09, index % 2 * 0.035, (index % 2 === 0 ? -1 : 1) * 0.045);

      if (trajectoryKind === "harvest") {
        draw(cornTrajectory, plotSource, player, progress, 0.72, Math.PI * 2.1);
      } else if (trajectoryKind === "stage") {
        for (let index = 0; index < unitCount; index += 1) {
          const delta = offset(index);
          draw(cornTrajectory, player.clone().add(delta), feed.clone().add(delta), progress, 0.95 + index * 0.05, Math.PI * 1.7);
        }
      } else if (trajectoryKind === "egg-pop") {
        for (let index = 0; index < unitCount; index += 1) {
          const delta = offset(index);
          draw(eggTrajectory, nest.clone().add(delta), nest.clone().add(delta).add(new THREE.Vector3(0, 0.14, 0)), progress, 0.88 + index * 0.05, Math.PI * 1.1);
        }
      } else if (trajectoryKind === "collect") {
        draw(eggTrajectory, nest, player, progress, 0.78, Math.PI * 1.45);
      } else if (trajectoryKind === "sale") {
        for (let index = 0; index < unitCount; index += 1) {
          const delta = offset(index);
          draw(eggTrajectory, player.clone().add(delta), marketReceiver.clone().add(delta), progress, 0.95 + index * 0.05, Math.PI * 1.85);
        }
        const cashProgress = THREE.MathUtils.clamp((progress - 0.22) / 0.78, 0, 1);
        draw(cashTrajectory, marketReceiver, cashLanding, cashProgress, 0.68, Math.PI * 1.05);
      } else if (trajectoryKind === "pad-ready") {
        draw(cashTrajectory, cashLanding, penPad, progress, 0.82, Math.PI * 1.05);
      }
    }
    for (const pool of pools) pool.mesh.instanceMatrix.needsUpdate = true;
  }

  function updateManualVfx(elapsedSeconds: number): void {
    const hidden = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let index = 0; index < manualVfxCapacity; index += 1) {
      leafVfx.setMatrixAt(index, hidden);
      rewardVfx.setMatrixAt(index, hidden);
    }
    if (reducedMotion) {
      leafVfx.instanceMatrix.needsUpdate = true;
      rewardVfx.instanceMatrix.needsUpdate = true;
      return;
    }
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    let leafIndex = 0;
    let rewardIndex = 0;
    for (const effect of manualEffects) {
      const age = elapsedSeconds - effect.bornAtSeconds;
      if (age < 0 || age > 1.05) continue;
      const progress = age / 1.05;
      const reward = /sale|cash|pad|extension|egg|tend/i.test(effect.kind);
      for (let particle = 0; particle < 7; particle += 1) {
        const targetIndex = reward ? rewardIndex++ : leafIndex++;
        if (targetIndex >= manualVfxCapacity) continue;
        const angle = particle * Math.PI * 0.35 + effect.eventId * 0.37;
        const radius = 0.16 + progress * 0.62;
        position.copy(effect.anchor).add(new THREE.Vector3(
          Math.cos(angle) * radius,
          Math.sin(progress * Math.PI) * (0.42 + particle * 0.045),
          Math.sin(angle) * radius,
        ));
        quaternion.setFromEuler(new THREE.Euler(progress * 5 + angle, angle, progress * 4));
        scale.setScalar(Math.max(0.01, (1 - progress) * (reward ? 1.25 : 1.05)));
        vfxMatrix.compose(position, quaternion, scale);
        (reward ? rewardVfx : leafVfx).setMatrixAt(targetIndex, vfxMatrix);
      }
    }
    leafVfx.instanceMatrix.needsUpdate = true;
    rewardVfx.instanceMatrix.needsUpdate = true;
  }

  function animateScene(alpha: number, elapsedSeconds: number, renderFacing: number): void {
    const phase = simulationSeconds + alpha * FIXED_STEP_SECONDS;
    resetAnimationParts(playerParts);
    resetAnimationParts(chickenParts);
    resetAnimationParts(cornParts);
    resetAnimationParts(padParts);
    resetAnimationParts(cargoParts);
    resetAnimationParts(stagedCornParts);
    resetAnimationParts(nestEggParts);
    resetAnimationParts(marketReceiverParts);
    resetAnimationParts(penPadParts);

    if (!reducedMotion) {
      const stride = moving ? Math.sin(phase * 14.5) : 0;
      kit.player.position.y = playerBaseY + (moving ? Math.abs(Math.sin(phase * 14.5)) * 0.075 : Math.sin(elapsedSeconds * 1.65) * 0.02);
      kit.player.rotation.y = renderFacing;
      kit.player.rotation.z = playerBaseRotationZ + (moving ? stride * 0.032 : 0);
      kit.player.scale.set(
        1 + (moving ? Math.abs(stride) * 0.02 : 0),
        1 - (moving ? Math.abs(stride) * 0.03 : 0),
        1 + (moving ? Math.abs(stride) * 0.02 : 0),
      );
      for (const part of playerParts) {
        const lowerName = part.object.name.toLowerCase();
        if (lowerName.includes("left-boot") || lowerName.includes("left-foot") || lowerName.includes("left-leg")) {
          part.object.rotation.x += stride * 0.58;
        } else if (lowerName.includes("right-boot") || lowerName.includes("right-foot") || lowerName.includes("right-leg")) {
          part.object.rotation.x -= stride * 0.58;
        } else if (lowerName.includes("hand") || lowerName.includes("arm")) {
          part.object.rotation.x -= stride * 0.28;
        }
      }
      chickenParts.forEach((part, index) => {
        const peck = Math.max(0, Math.sin(elapsedSeconds * (2.55 + (index % 3) * 0.2) + index * 1.7));
        part.object.rotation.x += peck * 0.12;
        part.object.position.y += Math.sin(elapsedSeconds * 2.3 + index * 2.2) * 0.012;
      });
      cornParts.forEach((part, index) => {
        part.object.rotation.z += Math.sin(elapsedSeconds * 1.55 + index * 0.61) * 0.032;
        part.object.rotation.x += Math.cos(elapsedSeconds * 1.1 + index * 0.43) * 0.014;
      });
      padParts.forEach((part, index) => {
        const pulse = 1 + Math.sin(elapsedSeconds * 3.4 + index * 0.22) * 0.065;
        part.object.scale.copy(part.scale).multiplyScalar(pulse);
      });
      if (manualSnapshot !== null) {
        const interaction = manualSnapshot.interaction;
        if (interaction !== null) {
          const progress = THREE.MathUtils.clamp(interaction.progress01, 0, 1);
          const reach = Math.sin(progress * Math.PI);
          if (interaction.kind === "harvest" || interaction.kind === "collect" || interaction.kind === "sale") {
            visualKit.player.leftArm.rotation.x -= reach * 0.88;
            visualKit.player.rightArm.rotation.x -= reach * 0.72;
            visualKit.player.body.rotation.x = reach * 0.14;
            kit.player.scale.y = 1 - reach * 0.06;
            kit.player.scale.x = 1 + reach * 0.04;
          }
          if (interaction.kind === "stage") {
            cargoParts.forEach((part, index) => {
              if (!part.object.visible) return;
              part.object.position.y += reach * (0.09 + index * 0.012);
              part.object.rotation.x -= reach * 0.28;
            });
            visualKit.player.leftArm.rotation.x -= reach * 0.72;
            visualKit.player.rightArm.rotation.x -= reach * 0.72;
          }
          if (interaction.kind === "tend") {
            visualKit.player.leftArm.rotation.x -= reach * 0.52;
            visualKit.player.rightArm.rotation.x -= (1 - reach) * 0.52;
            chickenParts.forEach((part, index) => {
              part.object.rotation.x += Math.sin(phase * 18 + index) * reach * 0.12;
            });
          }
          if (interaction.kind === "sale") {
            kit.manualChain.marketReceiver.scale.setScalar(1 + reach * 0.14);
            kit.manualChain.marketCash.scale.setScalar(1 + reach * 0.18);
          }
        } else {
          kit.manualChain.marketReceiver.scale.setScalar(1);
          kit.manualChain.marketCash.scale.setScalar(1);
        }
        cargoParts.forEach((part, index) => {
          if (!part.object.visible) return;
          part.object.rotation.z += Math.sin(phase * 9.5 + index * 0.42) * (moving ? 0.08 : 0.022);
          part.object.position.y += Math.sin(phase * 10 + index * 0.7) * (moving ? 0.032 : 0.012);
        });
        if (manualSnapshot.pen.tending) {
          chickenParts.forEach((part, index) => {
            const peck = Math.max(0, Math.sin(phase * 18 + index * 1.3));
            part.object.rotation.x += peck * 0.38;
            part.object.position.y += Math.abs(Math.sin(phase * 14 + index)) * 0.045;
          });
        }
        kit.manualChain.plots.forEach((plot, index) => {
          if (plot.cooldownCue.visible) {
            const progress = THREE.MathUtils.clamp(manualSnapshot!.plots[index]?.cooldown01 ?? 0, 0, 1);
            plot.cooldownCue.scale.setScalar(0.78 + progress * 0.28);
          }
        });
        if (manualSnapshot.pad.state === "active") {
          penPadParts.forEach((part, index) => {
            const pulse = 1 + Math.sin(elapsedSeconds * 4.2 + index * 0.4) * 0.08;
            part.object.scale.copy(part.scale).multiplyScalar(pulse);
          });
        }
      }
    } else {
      kit.player.position.y = playerBaseY;
      kit.player.rotation.y = renderFacing;
      kit.player.rotation.z = playerBaseRotationZ;
      kit.player.scale.set(1, 1, 1);
    }

    updateManualTrajectories();
    updateManualVfx(elapsedSeconds);
    juice.update(elapsedSeconds, reducedMotion);

    const dustTime = simulationSeconds + alpha * FIXED_STEP_SECONDS;
    for (const particle of dustParticles) {
      const age = dustTime - particle.bornAtSeconds;
      const active = !reducedMotion && age >= 0 && age < 0.52;
      particle.mesh.visible = active;
      if (!active) {
        particle.mesh.material.opacity = 0;
        continue;
      }
      const progress = age / 0.52;
      particle.mesh.material.opacity = (1 - progress) * 0.34;
      particle.mesh.scale.setScalar(0.55 + progress * 1.55);
    }
  }

  function updateOcclusion(deltaSeconds: number): void {
    kit.player.getWorldPosition(playerWorldPosition);
    const direction = playerWorldPosition.clone().sub(camera.position);
    const playerDistance = direction.length();
    if (playerDistance <= Number.EPSILON) {
      return;
    }
    direction.divideScalar(playerDistance);
    raycaster.set(camera.position, direction);
    raycaster.near = 0;
    raycaster.far = playerDistance;
    const hits = new Set(
      raycaster.intersectObjects([...kit.occluders], true).map((intersection) => intersection.object),
    );
    for (const occluder of kit.occluders) {
      let obscuresPlayer = false;
      occluder.traverse((object) => {
        if (hits.has(object)) {
          obscuresPlayer = true;
        }
      });
      forEachMaterial(occluder, (material) => {
        const original = occluderMaterialStates.get(material);
        if (original === undefined) {
          return;
        }
        const targetOpacity = obscuresPlayer ? original.opacity * 0.18 : original.opacity;
        const damping = obscuresPlayer ? 0.1 : 0.18;
        const blend = deltaSeconds <= 0 ? 1 : 1 - Math.exp(-deltaSeconds / damping);
        material.opacity = THREE.MathUtils.lerp(material.opacity, targetOpacity, blend);
        material.transparent = obscuresPlayer || original.transparent;
        material.depthWrite = obscuresPlayer ? false : original.depthWrite;
        material.needsUpdate = true;
      });
    }
  }

  const controller: FirstYardSceneController = {
    scene,
    camera,

    projectManualChain(snapshot, events): void {
      if (disposed) return;
      manualSnapshot = snapshot;
      reducedMotion = snapshot.reducedMotion;
      previousX = currentX;
      previousZ = currentZ;
      previousFacing = currentFacing;
      currentX = snapshot.player.position.x;
      currentZ = snapshot.player.position.z;
      currentFacing = snapshot.player.facingRadians;
      moving = snapshot.player.moving;
      simulationSeconds = snapshot.tick * FIXED_STEP_SECONDS;
      applyManualSnapshot(snapshot);
      for (const event of events) {
        if (seenManualEventIds.has(event.id)) continue;
        seenManualEventIds.add(event.id);
        const anchor = effectAnchor(event.kind);
        const bornAt = event.tick * FIXED_STEP_SECONDS;
        manualEffects.push({
          anchor,
          bornAtSeconds: bornAt,
          eventId: event.id,
          kind: event.kind,
          playerOrigin: new THREE.Vector3(currentX, 1.15, currentZ),
          plotId: event.plotId,
          quantity: event.quantity,
        });
        const kind = event.kind;
        if (/harvest/i.test(kind)) {
          juice.burst({ x: anchor.x, y: anchor.y, z: anchor.z, kind: "harvest", intensity: 1.1, bornAtSeconds: bornAt });
        } else if (/sale|cash/i.test(kind)) {
          juice.burst({ x: anchor.x, y: anchor.y + 0.2, z: anchor.z, kind: "sale", intensity: 1.55, bornAtSeconds: bornAt });
          juice.punch(1.35);
        } else if (/pad|extension|afford/i.test(kind)) {
          juice.burst({ x: anchor.x, y: anchor.y + 0.15, z: anchor.z, kind: "unlock", intensity: 1.4, bornAtSeconds: bornAt });
          juice.punch(1.1);
        } else if (/tend|egg|collect/i.test(kind)) {
          juice.burst({ x: anchor.x, y: anchor.y, z: anchor.z, kind: "reward", intensity: 0.95, bornAtSeconds: bornAt });
        } else {
          juice.burst({ x: anchor.x, y: anchor.y, z: anchor.z, kind: "reward", intensity: 0.75, bornAtSeconds: bornAt });
        }
      }
      while (manualEffects.length > manualVfxCapacity) manualEffects.shift();
    },

    stepFixed(intent, stepSeconds): void {
      if (disposed) {
        return;
      }
      if (!Number.isFinite(stepSeconds) || Math.abs(stepSeconds - FIXED_STEP_SECONDS) > 1e-9) {
        throw new RangeError("First-yard preview movement requires an exact 0.05 second fixed step");
      }
      const normalized = normalizedIntent(intent);
      previousX = currentX;
      previousZ = currentZ;
      previousFacing = currentFacing;
      const distance = PLAYER_SPEED_WU_PER_SECOND * stepSeconds;
      const next = clampToWalkableAxis(
        currentX,
        currentZ,
        normalized.x * distance,
        normalized.z * distance,
      );
      currentX = next.x;
      currentZ = next.z;
      moving = Math.hypot(currentX - previousX, currentZ - previousZ) > 1e-9;
      if (moving) {
        currentFacing = Math.atan2(normalized.x, normalized.z);
        dustDistanceAccumulator += Math.hypot(currentX - previousX, currentZ - previousZ);
        if (!reducedMotion && dustDistanceAccumulator >= 0.48) {
          dustDistanceAccumulator %= 0.48;
          emitDust(previousX, previousZ);
        }
      }
      simulationSeconds += stepSeconds;
      currentSnapshot = freezeSnapshot(currentX, currentZ, currentFacing, moving, reducedMotion);
    },

    render(alpha, elapsedSeconds): void {
      if (disposed) {
        return;
      }
      if (!Number.isFinite(alpha) || !Number.isFinite(elapsedSeconds)) {
        throw new RangeError("Render interpolation values must be finite");
      }
      const interpolation = THREE.MathUtils.clamp(alpha, 0, 1);
      const renderX = THREE.MathUtils.lerp(previousX, currentX, interpolation);
      const renderZ = THREE.MathUtils.lerp(previousZ, currentZ, interpolation);
      const renderFacing = previousFacing + shortestAngle(previousFacing, currentFacing) * interpolation;
      kit.player.position.x = renderX;
      kit.player.position.z = renderZ;
      animateScene(interpolation, elapsedSeconds, renderFacing);

      const deltaSeconds =
        lastRenderElapsedSeconds === null || elapsedSeconds < lastRenderElapsedSeconds
          ? 0
          : Math.min(0.1, elapsedSeconds - lastRenderElapsedSeconds);
      lastRenderElapsedSeconds = elapsedSeconds;
      computeDesiredCameraTarget(renderX, renderZ, renderFacing);
      if (reducedMotion || deltaSeconds === 0) {
        cameraTarget.copy(desiredCameraTarget);
      } else {
        cameraTarget.lerp(desiredCameraTarget, 1 - Math.exp(-deltaSeconds / CAMERA_DAMPING_SECONDS));
      }
      fitCameraDistance(cameraTarget, renderX, renderZ);
      updateCameraPosition(cameraTarget);
      juice.applyCameraPunch(camera, deltaSeconds);
      updateOcclusion(deltaSeconds);
      renderer.render(scene, camera);
    },

    resize(width, height, pixelRatio): void {
      if (disposed) {
        return;
      }
      if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        throw new RangeError("Scene dimensions must be finite and positive");
      }
      if (!Number.isFinite(pixelRatio) || pixelRatio <= 0) {
        throw new RangeError("Pixel ratio must be finite and positive");
      }
      camera.aspect = width / height;
      camera.fov = CAMERA_FOV_DEGREES;
      baseCameraDistance = 13.5;
      camera.updateProjectionMatrix();
      renderer.setPixelRatio(Math.min(pixelRatio, MAX_PIXEL_RATIO));
      renderer.setSize(Math.max(1, Math.round(width)), Math.max(1, Math.round(height)), false);
      fitCameraDistance(cameraTarget, currentX, currentZ);
      updateCameraPosition(cameraTarget);
    },

    setReducedMotion(nextReducedMotion): void {
      if (disposed || reducedMotion === nextReducedMotion) {
        return;
      }
      reducedMotion = nextReducedMotion;
      if (reducedMotion) {
        dustDistanceAccumulator = 0;
        for (const particle of dustParticles) {
          particle.bornAtSeconds = Number.NEGATIVE_INFINITY;
          particle.mesh.visible = false;
          particle.mesh.material.opacity = 0;
        }
      }
      currentSnapshot = freezeSnapshot(currentX, currentZ, currentFacing, moving, reducedMotion);
    },

    snapshot(): Readonly<WalkaboutPresentationSnapshot> {
      return currentSnapshot;
    },

    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      for (const [material, original] of occluderMaterialStates) {
        material.opacity = original.opacity;
        material.transparent = original.transparent;
        material.depthWrite = original.depthWrite;
        material.needsUpdate = true;
      }
      dustGeometry.dispose();
      for (const particle of dustParticles) {
        particle.mesh.material.dispose();
      }
      leafVfxGeometry.dispose();
      leafVfxMaterial.dispose();
      rewardVfxGeometry.dispose();
      rewardVfxMaterial.dispose();
      juice.dispose();
      kit.dispose();
      for (const material of clonedOccluderMaterials) {
        material.dispose();
      }
      for (const material of detachedOwnedMaterials) {
        material.dispose();
      }
      scene.clear();
      shadowMap.enabled = originalShadowEnabled;
      shadowMap.type = originalShadowType;
      // canvas and renderer remain owned by the caller.
      void canvas;
    },
  };

  const initialWidth = Math.max(1, canvas.clientWidth || canvas.width || 1);
  const initialHeight = Math.max(1, canvas.clientHeight || canvas.height || 1);
  controller.resize(initialWidth, initialHeight, 1);
  return controller;
}
